import { GanttUser, Dependency } from '../types/gantt.types'
import { useInvalidateSignals } from './useTaskSignals'
import { useState, useRef, useCallback, useEffect } from 'react'
import { apiClient } from '@indipia/shared'
import { toast } from 'sonner'
import { addPredecessorId, buildPredecessorPatch, INVALID_PREDECESSOR_MESSAGE, isInvalidPredecessorError, predecessorsOf, removePredecessorId } from '../views/scheduleUtils'
// 선후관계 연결선(커넥터 드래그 · 낙관 갱신 · Delete 키 삭제) — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttConnectorArgs = {
  user: GanttUser
  depsRef: React.MutableRefObject<Dependency[]>
  setDeps: React.Dispatch<React.SetStateAction<Dependency[]>>
  invalidateSignals: ReturnType<typeof useInvalidateSignals>
  setCtxMenu: React.Dispatch<React.SetStateAction<{ x: number; y: number; taskId: number; taskName: string; taskEndDate: Date; } | null>>
  chartOverlayRef: React.RefObject<SVGSVGElement>
}

export function useGanttConnector({
  user, depsRef, setDeps, invalidateSignals, setCtxMenu, chartOverlayRef,
}: UseGanttConnectorArgs) {
  // 선후관계 선 잇기 — 바 위/아래 작은 원(커넥터)을 드래그해서 다른 바까지 연결하면 선행-후행
  // 관계가 생긴다. x1/y1은 오버레이 svg 내부 좌표(스크롤과 무관, getBoundingClientRect 기준).
  const [connectDrag, setConnectDrag] = useState<{
    fromTaskId: number; fromEdge: 'top' | 'bottom'; x0: number; y0: number; x1: number; y1: number
  } | null>(null)

  // 선택된 선후관계 화살표 — 클릭으로 선택하고 Backspace/Delete로 삭제한다.
  const [selectedDepId, setSelectedDepId] = useState<number | null>(null)

  // connectDragState: 선후관계 커넥터 드래그 — 시작 taskId/edge만 들고 있고, 실시간 좌표(x1/y1)는
  // connectDrag state로 관리(미리보기 선을 다시 그려야 하므로). 문서 레벨 mousemove/mouseup에서
  // 클로저 갱신 문제 없이 최신 값을 읽기 위한 ref.
  const connectDragState = useRef<{ fromTaskId: number; fromEdge: 'top' | 'bottom' } | null>(null)

  // ── 선후관계 선 잇기(커넥터 드래그) ─────────────────────────────
  const connectHeaders = () => ({
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  })

  // 선행 한 개를 더하거나(mode 'add') 한 개만 뺀다(mode 'remove'). 서버는 다중 선행이라 대상 공종의 선행
  // 목록 전체(predecessorTaskIds)와 updatePredecessor=true를 보내 그 목록으로 바꾼다 — 기존 선행은 보존된다.
  // setRefreshKey(전체 재조회)는 쓰지 않는다 — loading=true가 간트차트 전체를 "불러오는 중..."으로
  // 갈아치웠다가 응답이 와야 되돌아와서 화면이 깜빡였다. 대신 deps를 즉시 로컬로 갱신(낙관적 업데이트)하고
  // 실패하면 되돌린다. 순환이면 서버가 400 INVALID_PREDECESSOR로 거절하며 이때도 되돌리고 문구를 보여 준다.
  // dependencyId는 이 세션 안에서 화살표 key·선택 매칭용으로만 쓰이므로 새 선행은 임시 id를 쓴다.
  const patchPredecessor = async (successorTaskId: number, predecessorTaskId: number, mode: 'add' | 'remove') => {
    const prevDeps = depsRef.current
    const current = predecessorsOf(prevDeps, successorTaskId)
    if (mode === 'add' && current.includes(predecessorTaskId)) return // 이미 같은 선행이 있으면 보내지 않는다
    const nextIds = mode === 'add' ? addPredecessorId(current, predecessorTaskId) : removePredecessorId(current, predecessorTaskId)
    setDeps(prev => {
      const kept = prev.filter(d => d.successorTaskId !== successorTaskId || nextIds.includes(d.predecessorTaskId))
      const base = Date.now()
      const added = nextIds
        .filter(id => !kept.some(d => d.successorTaskId === successorTaskId && d.predecessorTaskId === id))
        .map((id, i) => ({ dependencyId: base + i, predecessorTaskId: id, successorTaskId, dependencyType: 'FS' as const }))
      return [...kept, ...added]
    })
    try {
      await apiClient.patch(`/schedule/tasks/${successorTaskId}`, buildPredecessorPatch(nextIds), { headers: connectHeaders() })
      void invalidateSignals()
    } catch (err) {
      setDeps(prevDeps)
      toast.error(isInvalidPredecessorError(err) ? INVALID_PREDECESSOR_MESSAGE
        : mode === 'add' ? '선후관계 연결 중 오류가 발생했습니다.' : '선후관계 삭제 중 오류가 발생했습니다.')
    }
  }

  const onConnectorMouseDown = useCallback((e: React.MouseEvent, taskId: number, edge: 'top' | 'bottom', x0: number, y0: number) => {
    if (e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()
    setCtxMenu(null)
    setSelectedDepId(null)
    connectDragState.current = { fromTaskId: taskId, fromEdge: edge }
    document.body.style.cursor     = 'pointer'
    document.body.style.userSelect = 'none'
    setConnectDrag({ fromTaskId: taskId, fromEdge: edge, x0, y0, x1: x0, y1: y0 })
  }, [])

  // 드롭 대상 원(circle) 자체의 onMouseUp에서 호출 — stopPropagation으로 문서 레벨 mouseup(취소
  // 처리)까지 전파되지 않게 막는다. edge가 드래그 방향과 맞물리지 않으면(예: 후보로 안 걸러진
  // 경우) 조용히 무시한다.
  // useCallback으로 고정하지 않는다 — patchPredecessor가 매 렌더 user를 새로 캡처하므로,
  // 여기서도 항상 최신 클로저를 쓰도록 그냥 일반 함수로 둔다(JSX가 어차피 매 렌더 새로 만듦).
  const onConnectorDrop = (targetTaskId: number, targetEdge: 'top' | 'bottom') => {
    const st = connectDragState.current
    connectDragState.current = null
    document.body.style.cursor = ''; document.body.style.userSelect = ''
    setConnectDrag(null)
    if (!st || targetTaskId === st.fromTaskId) return
    let predecessorTaskId: number, successorTaskId: number
    if (st.fromEdge === 'bottom') {
      if (targetEdge !== 'top') return
      predecessorTaskId = st.fromTaskId; successorTaskId = targetTaskId
    } else {
      if (targetEdge !== 'bottom') return
      predecessorTaskId = targetTaskId; successorTaskId = st.fromTaskId
    }
    void patchPredecessor(successorTaskId, predecessorTaskId, 'add')
  }

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!connectDragState.current || !chartOverlayRef.current) return
      const rect = chartOverlayRef.current.getBoundingClientRect()
      const x1 = e.clientX - rect.left, y1 = e.clientY - rect.top
      setConnectDrag(prev => (prev ? { ...prev, x1, y1 } : prev))
    }
    const onUp = () => {
      if (!connectDragState.current) return
      // 원(circle) 위에서 놓였으면 그 circle의 onMouseUp이 stopPropagation으로 여기까지 오는 걸
      // 막는다 — 여기 도달했다는 건 빈 공간에 놓였다는 뜻이므로 그냥 취소.
      connectDragState.current = null
      document.body.style.cursor = ''; document.body.style.userSelect = ''
      setConnectDrag(null)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup',   onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
    }
  }, [])

  // 선택된 선후관계를 Backspace/Delete로 삭제 — 입력창에 포커스가 있으면 무시(텍스트 편집 방해 방지).
  useEffect(() => {
    if (selectedDepId === null) return
    const onKeyDown = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return
      if (e.key !== 'Backspace' && e.key !== 'Delete') return
      e.preventDefault()
      const dep = depsRef.current.find(d => d.dependencyId === selectedDepId)
      setSelectedDepId(null)
      if (dep) void patchPredecessor(dep.successorTaskId, dep.predecessorTaskId, 'remove')
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [selectedDepId])

  return { setSelectedDepId, connectDrag, selectedDepId, onConnectorMouseDown, onConnectorDrop }
}
